import { clsx, type ClassValue } from "clsx";

/**
 * Theme configuration for the design system
 */
export const theme = {
  colors: {
    // Semantic color mappings
    background: {
      DEFAULT: "bg-background",
      secondary: "bg-secondary",
      muted: "bg-muted",
      accent: "bg-accent",
      card: "bg-card",
      popover: "bg-popover",
    },
    foreground: {
      DEFAULT: "text-foreground",
      secondary: "text-secondary-foreground",
      muted: "text-muted-foreground",
      accent: "text-accent-foreground",
      card: "text-card-foreground",
      popover: "text-popover-foreground",
    },
    border: {
      DEFAULT: "border-border",
      input: "border-input",
      sidebar: "border-sidebar-border",
    },
    ring: {
      DEFAULT: "ring-ring",
      sidebar: "ring-sidebar-ring",
    },
  },
  radius: {
    none: "rounded-none",
    sm: "rounded-sm",
    DEFAULT: "rounded-md",
    md: "rounded-md",
    lg: "rounded-lg",
    xl: "rounded-xl",
    "2xl": "rounded-2xl",
    "3xl": "rounded-3xl",
    full: "rounded-full",
  },
  shadow: {
    sm: "shadow-sm",
    DEFAULT: "shadow",
    md: "shadow-md",
    lg: "shadow-lg",
    xl: "shadow-xl",
    "2xl": "shadow-2xl",
    none: "shadow-none",
  },
  transition: {
    DEFAULT: "transition-all duration-200",
    fast: "transition-all duration-100",
    slow: "transition-all duration-300",
    none: "",
  },
} as const;

/**
 * Generate consistent focus styles
 */
export function focusRing(className?: ClassValue) {
  return clsx(
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    className,
  );
}

/**
 * Generate consistent disabled styles
 */
export function disabled(className?: ClassValue) {
  return clsx("disabled:cursor-not-allowed disabled:opacity-50", className);
}

/**
 * Typography presets
 */
export const typography = {
  h1: "scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl",
  h2: "scroll-m-20 text-3xl font-semibold tracking-tight",
  h3: "scroll-m-20 text-2xl font-semibold tracking-tight",
  h4: "scroll-m-20 text-xl font-semibold tracking-tight",
  p: "leading-7 [&:not(:first-child)]:mt-6",
  blockquote: "mt-6 border-l-2 pl-6 italic",
  lead: "text-xl text-muted-foreground",
  large: "text-lg font-semibold",
  small: "text-sm font-medium leading-none",
  muted: "text-sm text-muted-foreground",
} as const;

/**
 * Animation presets
 */
export const animation = {
  fadeIn: "animate-in fade-in-0",
  fadeOut: "animate-out fade-out-0",
  slideInFromTop: "animate-in slide-in-from-top-2",
  slideInFromBottom: "animate-in slide-in-from-bottom-2",
  slideInFromLeft: "animate-in slide-in-from-left-2",
  slideInFromRight: "animate-in slide-in-from-right-2",
  slideOutToTop: "animate-out slide-out-to-top-2",
  slideOutToBottom: "animate-out slide-out-to-bottom-2",
  slideOutToLeft: "animate-out slide-out-to-left-2",
  slideOutToRight: "animate-out slide-out-to-right-2",
  zoomIn: "animate-in zoom-in-90",
  zoomOut: "animate-out zoom-out-90",
  spin: "animate-spin",
  pulse: "animate-pulse",
  bounce: "animate-bounce",
} as const;

/**
 * Container presets
 */
export const container = {
  center: "container mx-auto",
  padding: {
    none: "",
    sm: "px-4 sm:px-6",
    DEFAULT: "px-4 sm:px-6 lg:px-8",
    lg: "px-6 sm:px-8 lg:px-10",
  },
  maxWidth: {
    sm: "max-w-screen-sm",
    md: "max-w-screen-md",
    lg: "max-w-screen-lg",
    xl: "max-w-screen-xl",
    "2xl": "max-w-screen-2xl",
    "7xl": "max-w-7xl",
    full: "max-w-full",
  },
} as const;

/**
 * Grid system helpers
 */
export const grid = {
  cols: {
    1: "grid-cols-1",
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
    5: "grid-cols-5",
    6: "grid-cols-6",
    12: "grid-cols-12",
  },
  gap: {
    0: "gap-0",
    1: "gap-1",
    2: "gap-2",
    3: "gap-3",
    4: "gap-4",
    5: "gap-5",
    6: "gap-6",
    8: "gap-8",
  },
} as const;

/**
 * Flex system helpers
 */
export const flex = {
  direction: {
    row: "flex-row",
    col: "flex-col",
    rowReverse: "flex-row-reverse",
    colReverse: "flex-col-reverse",
  },
  wrap: {
    wrap: "flex-wrap",
    nowrap: "flex-nowrap",
    wrapReverse: "flex-wrap-reverse",
  },
  align: {
    start: "items-start",
    center: "items-center",
    end: "items-end",
    baseline: "items-baseline",
    stretch: "items-stretch",
  },
  justify: {
    start: "justify-start",
    center: "justify-center",
    end: "justify-end",
    between: "justify-between",
    around: "justify-around",
    evenly: "justify-evenly",
  },
  gap: {
    0: "gap-0",
    1: "gap-1",
    2: "gap-2",
    3: "gap-3",
    4: "gap-4",
    5: "gap-5",
    6: "gap-6",
    8: "gap-8",
  },
} as const;
